import { useCallback, useEffect, useState } from "react";
import { useBlocker } from "react-router";
import { useBeforeUnload } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DndContext, useSensor, useSensors, PointerSensor, KeyboardSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import Button from "@shared/v2/Button";
import { Popover, PopoverItem } from "@shared/v2/Popover";
import TextLink from "@shared/v2/TextLink";
import Typography from "@shared/v2/Typography";
import {
  ArrowUpRightFromSquareSolidV6,
  ChevronDownSolidV6,
  PlusSolidV6,
  TriangleExclamationSolidV6,
} from "@shared/v2/Icomoon";
import WebsiteHeader from "../shared/WebsiteHeader";
import AboutWebsiteProfilesModal from "./AboutWebsiteProfilesModal";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import AgentLenderCard, { SortableAgentLenderCard } from "./AgentLenderCard";
import {
  getDirtyState,
  getRoster,
  getSeatsAvailable,
  publishRoster,
  sortRoster,
} from "../../reducers/websiteReducer/websiteReducer";
import PersonDetailsModal from "../../Contacts/PersonDetailsModal";
import {
  useRoster,
  useRosterLoading,
  useSeatsAvailable,
  useSortRosterLoading,
  useWebsiteIDXUrl,
} from "../../reducers/websiteReducer/selectors";
import NoSeatsRemainingModal from "./NoSeatsRemainingModal";
import DeleteAgentLenderModal from "./DeleteAgentLenderModal";
import FeaturedEmptyState from "./FeaturedEmptyState";
import Loading from "../../Goals/components/Loader";
import UnpublishedChangesModal from "./UnpublishedChangesModal";

const ADD_OPTIONS = [
  {
    label: "New Website Profile",
    value: "website_profile",
    meta: { description: "Add someone to your website without granting access to the CRM" },
  },
  {
    label: "New CRM User",
    value: "crm_user",
    meta: { description: "Invite a new agent to your CRM and display them on the website" },
  },
];

const SORT_OPTIONS = [
  { label: "First Name (A → Z)", value: "first_name", meta: { dir: "asc" } },
  { label: "First Name (Z → A)", value: "first_name", meta: { dir: "desc" } },
  { label: "Last Name (A → Z)", value: "last_name", meta: { dir: "asc" } },
  { label: "Last Name (Z → A)", value: "last_name", meta: { dir: "desc" } },
  { label: "Date Added (Asc.)", value: "state", meta: { dir: "asc" } },
  { label: "Date Added (Desc.)", value: "state", meta: { dir: "desc" } },
  { label: "Random", value: "random" },
  { label: "Custom", value: "manual" },
];

const FEATURED_ID = "featured";
const TEAM_ID = "team";

const Roster = () => {
  const dispatch = useDispatch();
  const websiteUrl = useWebsiteIDXUrl();
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showNoSeatsModal, setShowNoSeatsModal] = useState(false);
  const [personDetailsModal, setPersonDetailsModal] = useState({ open: false, personId: null, type: null });
  const [showDeleteLenderModal, setShowDeleteLenderModal] = useState(null);
  const [showDeleteAgentModal, setShowDeleteAgentModal] = useState(null);
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[6]);
  const seatsAvailable = useSeatsAvailable();
  const rosterLoading = useRosterLoading();
  const sortRosterLoading = useSortRosterLoading();
  const roster = useRoster();
  const [featuredAgents, setFeaturedAgents] = useState(roster.featured);
  const [teamAgents, setTeamAgents] = useState(roster.roster);

  const [activeId, setActiveId] = useState(null);

  const [overContainer, setOverContainer] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => {
    dispatch(getRoster());
    dispatch(getSeatsAvailable());
  }, []);

  useEffect(() => {
    if (roster.accountOrder?.key === null) return;
    setSortOption(
      SORT_OPTIONS.find(
        (o) => o.value === roster.accountOrder?.key && (!o.meta || o.meta.dir === roster.accountOrder.order),
      ) || SORT_OPTIONS[6],
    );
  }, [roster.accountOrder]);

  useEffect(() => {
    setFeaturedAgents(roster.featured);
    setTeamAgents(roster.roster);
  }, [roster.featured, roster.roster]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const findAgentById = (uuid) =>
    roster.featured.find((a) => a.personUuid === uuid) || roster.roster.find((a) => a.personUuid === uuid);

  const getListAndIndex = (uuid) => {
    const featuredIdx = featuredAgents.findIndex((a) => a.personUuid === uuid);
    if (featuredIdx !== -1) return { list: FEATURED_ID, index: featuredIdx };
    const teamIdx = teamAgents.findIndex((a) => a.personUuid === uuid);
    if (teamIdx !== -1) return { list: TEAM_ID, index: teamIdx };
    return { list: null, index: -1 };
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (!over) {
      setOverContainer(null);
      setOverIndex(null);
      return;
    }
    if (over.id === "empty-state") {
      setOverContainer(FEATURED_ID);
      setOverIndex(-1);
      return;
    }
    if (over.id === FEATURED_ID) {
      setOverContainer(FEATURED_ID);
      setOverIndex(featuredAgents.length);
    } else if (over.id === TEAM_ID) {
      setOverContainer(TEAM_ID);
      setOverIndex(teamAgents.length);
    } else {
      const { list, index } = getListAndIndex(over.id);
      setOverContainer(list);
      setOverIndex(index);
    }
  };

  const handleDragEnd = (event) => {
    const { active } = event;
    setActiveId(null);

    if (!overContainer || overIndex === null || (sortOption.value !== "manual" && overContainer === TEAM_ID))
      return;

    const { list: fromList, index: fromIndex } = getListAndIndex(active.id);
    if (fromList === null || fromIndex === -1) return;

    let agent = findAgentById(active.id);

    const newFeatured = [...featuredAgents];
    const newTeam = [...teamAgents];
    if (fromList === FEATURED_ID) newFeatured.splice(fromIndex, 1);
    if (fromList === TEAM_ID) newTeam.splice(fromIndex, 1);

    if (overContainer === FEATURED_ID) {
      if (newFeatured.length >= 4) return;
      agent = { ...agent, isFeatured: true };
      newFeatured.splice(overIndex, 0, agent);
    } else if (overContainer === TEAM_ID) {
      agent = { ...agent, isFeatured: false };
      newTeam.splice(overIndex, 0, agent);
    }

    setFeaturedAgents(newFeatured);
    setTeamAgents(newTeam);
    dispatch(
      sortRoster({
        account_order: { key: sortOption.value, order: sortOption.meta?.dir || null },
        featured: newFeatured.map((a) => a.personUuid),
        roster: sortOption.value === "manual" ? newTeam.map((a) => a.personUuid) : undefined,
      }),
    );
    setOverContainer(null);
    setOverIndex(null);
  };

  const renderCardsWithIndicator = (agents, containerId) => {
    const cards = [];
    const { list: fromList } = getListAndIndex(activeId);
    let showIndicator = activeId && overContainer === containerId && fromList !== containerId;

    if (
      (containerId === FEATURED_ID && agents.length >= 4) ||
      (containerId === TEAM_ID && sortOption.value !== "manual")
    ) {
      showIndicator = false;
    }

    if (rosterLoading && agents.length === 0) {
      cards.push(
        <div className={roster.lender ? "tw-min-h-[236px]" : "tw-min-h-[108px]"} key="loading">
          <Loading />
        </div>,
      );
    }

    if (!rosterLoading && agents.length === 0 && containerId === FEATURED_ID) {
      cards.push(
        <FeaturedEmptyState
          hasLender={Boolean(roster.lender)}
          key="empty-state"
          sortKey={roster?.accountOrder?.key}
        />,
      );
    }

    for (let i = 0; i < agents.length; i += 1) {
      const agent = agents[i];
      const popoverOptions = [];
      if (agent.showAgentProfilePage === "true") {
        popoverOptions.push(
          <PopoverItem
            key="website"
            className="tw-whitespace-nowrap"
            onClick={() => {
              window.open(`${websiteUrl}/agent-profile/uuid/${agent.personUuid}`, "_blank");
            }}
          >
            Visit Profile Page
          </PopoverItem>,
        );
      }
      if (!agent.userUuid) {
        popoverOptions.push(
          <PopoverItem
            key="delete"
            className="tw-text-semantic-red-100 tw-text-nowrap"
            onClick={() => setShowDeleteAgentModal(agent)}
          >
            Remove Website Profile
          </PopoverItem>,
        );
      }

      if (showIndicator && overIndex === i) {
        cards.push(
          <div
            key={`${containerId}-indicator-start`}
            className="tw-h-full tw-w-[4px] tw-rounded-[4px] tw-bg-theme-primary"
          />,
        );
      }
      cards.push(
        <SortableAgentLenderCard
          key={agent.personUuid}
          id={agent.personUuid}
          disabled={sortRosterLoading || (containerId !== FEATURED_ID && sortOption.value !== "manual")}
          agent={agent}
          starDisabled={containerId === TEAM_ID && featuredAgents.length >= 4}
          onStar={() => {
            let newFeatured;
            let newTeam;
            if (sortRosterLoading) return;
            if (containerId === FEATURED_ID) {
              newFeatured = featuredAgents.filter((a) => a.personUuid !== agent.personUuid);
              newTeam = [...teamAgents, { ...agent, isFeatured: false }];
            } else {
              newFeatured = [...featuredAgents, { ...agent, isFeatured: true }];
              newTeam = teamAgents.filter((a) => a.personUuid !== agent.personUuid);
            }
            setFeaturedAgents(newFeatured);
            setTeamAgents(newTeam);
            dispatch(
              sortRoster({
                account_order: { key: sortOption.value, order: sortOption.meta?.dir || null },
                featured: newFeatured.map((a) => a.personUuid),
                roster: sortOption.value === "manual" ? newTeam.map((a) => a.personUuid) : undefined,
              }),
            );
          }}
          onEdit={() =>
            setPersonDetailsModal({
              open: true,
              personId: agent.id,
              type: agent.userUuid ? "user" : "websiteProfile",
            })
          }
          popoverOptions={popoverOptions.length ? popoverOptions : undefined}
        />,
      );
    }
    if (showIndicator && overIndex === agents.length) {
      cards.push(
        <div
          key={`${containerId}-indicator-end`}
          className="tw-h-full tw-w-[4px] tw-rounded-[4px] tw-bg-theme-primary"
        />,
      );
    }
    return cards;
  };

  const onBeforeUnload = useCallback(
    (e) => {
      if (!roster.dirty) return;
      e.preventDefault();
      e.returnValue =
        "Your roster has changes that have not been published to your Brivity website yet. You can either publish now or come back later.";
    },
    [roster.dirty],
  );

  useBeforeUnload(onBeforeUnload, { capture: true });

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => roster.dirty && currentLocation.pathname !== nextLocation.pathname,
  );

  return (
    <div className="tw-flex-1 tw-max-w-[1382px] tw-mx-auto tw-flex tw-flex-col tw-gap-[16px] tw-p-[16px]">
      <div className="tw-relative tw-flex tw-flex-col tw-gap-[32px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[24px] tw-overflow-auto">
        <WebsiteHeader
          title="Roster"
          description={
            <>
              Showcase your team on your Brivity website by adding agents, setting a preferred lender,
              customizing display order, and featuring key team members.{" "}
              <TextLink
                href="https://www.brivityknowledge.com/docs/manage-your-website-roster-and-team-profiles"
                target="_blank"
              >
                Learn more
              </TextLink>
              .
            </>
          }
          actions={
            <div className="tw-self-start tw-flex tw-items-center tw-gap-[8px]">
              {roster.dirty && (
                <div className="tw-flex tw-items-center tw-gap-[4px] tw-text-12d tw-text-[#E5941B]">
                  Unpublished Changes <TriangleExclamationSolidV6 />
                </div>
              )}
              <Button
                disabled={!roster.dirty}
                schema="secondary"
                size="medium"
                isLoading={roster.dirtyLoading}
                onClick={() =>
                  dispatch(publishRoster()).then(() => {
                    dispatch(getDirtyState());
                  })
                }
              >
                Publish Changes
              </Button>
              <Button
                size="medium"
                schema="tertiary"
                className="tw-whitespace-nowrap tw-flex tw-items-center tw-gap-[8px]"
                onClick={() => window.open(`${websiteUrl}/realestateagent/search`, "_blank")}
              >
                View Team Page <ArrowUpRightFromSquareSolidV6 />
              </Button>
              <Popover placement="bottom-end">
                <Popover.Trigger>
                  <Button size="medium" className="tw-whitespace-nowrap tw-flex tw-items-center tw-gap-[8px]">
                    <PlusSolidV6 /> Add To Roster
                  </Button>
                </Popover.Trigger>
                <Popover.Content>
                  {ADD_OPTIONS.map((option) => (
                    <Popover.Item
                      key={option.value}
                      className="!tw-h-auto"
                      onClick={() => {
                        if (option.value === "website_profile") {
                          if (localStorage.getItem("websiteRoster:hideAboutModal") === "true") {
                            setPersonDetailsModal({ open: true, personId: null, type: "websiteProfile" });
                          } else {
                            setShowAboutModal(true);
                          }
                        } else if (seatsAvailable) {
                          setPersonDetailsModal({ open: true, personId: null, type: "user" });
                        } else {
                          setShowNoSeatsModal(true);
                        }
                      }}
                    >
                      <div className="tw-flex tw-flex-col tw-gap-[4px] tw-max-w-[244px]">
                        {option.label}
                        <span className="tw-text-gray-40 tw-text-14d">{option.meta.description}</span>
                      </div>
                    </Popover.Item>
                  ))}
                </Popover.Content>
              </Popover>
            </div>
          }
        />
      </div>
      <DndContext
        sensors={sensors}
        onDragStart={(event) => setActiveId(event.active.id)}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="tw-flex tw-gap-[16px] tw-flex-wrap">
          <div className="tw-flex-1">
            <CollapsibleWebsiteCard
              title={`Featured Agents (${featuredAgents.length}/4)`}
              action={
                <span className="tw-text-12d tw-text-gray-40">
                  Featured agents appear larger and higher on the page than the rest of the team
                </span>
              }
            >
              <SortableContext
                id={FEATURED_ID}
                items={featuredAgents.length ? featuredAgents.map((a) => a.personUuid) : ["empty-state"]}
                strategy={rectSortingStrategy}
              >
                <div className="tw-min-h-[108px] tw-grid tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))] tw-gap-[24px]">
                  {renderCardsWithIndicator(featuredAgents, FEATURED_ID)}
                </div>
              </SortableContext>
            </CollapsibleWebsiteCard>
          </div>
          <CollapsibleWebsiteCard
            className="tw-basis-[300px]"
            panelClassName="tw-flex-1"
            title="Preferred Lender"
            allowCollapse={false}
            action={
              roster.lender ? null : (
                <Button
                  size="small"
                  onClick={() => setPersonDetailsModal({ open: true, personId: null, type: "lender" })}
                >
                  + Add
                </Button>
              )
            }
          >
            {!roster.lender && (
              <Typography className="!tw-text-gray-50">
                Add your preferred lender to feature them on the property details pages of your website!
              </Typography>
            )}
            {roster.lender && (
              <AgentLenderCard
                className="tw-max-w-full"
                lender={roster.lender}
                onEdit={() =>
                  setPersonDetailsModal({ open: true, personId: roster.lender.id, type: "lender" })
                }
                popoverOptions={[
                  <PopoverItem
                    key="website"
                    onClick={() =>
                      window.open(`${websiteUrl}/agent-profile/uuid/${roster.lender.personUuid}`, "_blank")
                    }
                  >
                    Visit Website
                  </PopoverItem>,
                  <PopoverItem
                    key="copy"
                    onClick={() =>
                      navigator.clipboard.writeText(
                        `${websiteUrl}/agent-profile/uuid/${roster.lender.personUuid}`,
                      )
                    }
                    className="tw-whitespace-nowrap"
                  >
                    Copy Website Link
                  </PopoverItem>,
                  <PopoverItem
                    key="delete"
                    className="tw-text-semantic-red-100"
                    onClick={() => setShowDeleteLenderModal(roster.lender)}
                  >
                    Remove Lender
                  </PopoverItem>,
                ]}
              />
            )}
          </CollapsibleWebsiteCard>
        </div>
        <div className="tw-flex tw-gap-[16px] tw-flex-wrap">
          <CollapsibleWebsiteCard
            className="tw-flex-1"
            title="Our Team"
            action={
              <div className="tw-flex tw-items-center tw-gap-[4px]">
                <span className="tw-text-12d tw-text-gray-40">Sort on website by</span>
                <Popover>
                  <Popover.Trigger>
                    <Button
                      size="small"
                      schema="secondary"
                      className="!tw-min-w-[140px] tw-flex tw-items-center"
                    >
                      <span className="tw-flex-1 tw-text-center">{sortOption.label}</span>
                      <ChevronDownSolidV6 className="tw-justify-self-end" size="s" />
                    </Button>
                  </Popover.Trigger>
                  <Popover.Content>
                    {SORT_OPTIONS.map((option) => (
                      <Popover.Item
                        className="tw-whitespace-nowrap"
                        key={`${option.value}-${option.meta?.dir || ""}`}
                        onClick={() => {
                          setSortOption(option);
                          dispatch(
                            sortRoster({
                              account_order: { key: option.value, order: option.meta?.dir || null },
                              featured: featuredAgents.map((a) => a.personUuid),
                              roster:
                                option.value === "manual" ? teamAgents.map((a) => a.personUuid) : undefined,
                            }),
                          );
                        }}
                      >
                        {option.label}
                      </Popover.Item>
                    ))}
                  </Popover.Content>
                </Popover>
              </div>
            }
            allowCollapse={false}
          >
            <SortableContext
              id={TEAM_ID}
              items={teamAgents.map((a) => a.personUuid)}
              strategy={rectSortingStrategy}
            >
              <div className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(200px,1fr))] tw-gap-[24px]">
                {renderCardsWithIndicator(teamAgents, TEAM_ID)}
              </div>
            </SortableContext>
          </CollapsibleWebsiteCard>
          <div className="tw-basis-[300px]" />
        </div>
        <DragOverlay>
          {activeId ? (
            <AgentLenderCard
              agent={findAgentById(activeId)}
              className="tw-border-theme-primary tw-shadow-card tw-shadow-theme-primary"
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <AboutWebsiteProfilesModal
        show={showAboutModal}
        onClose={() => {
          setShowAboutModal(false);
          setPersonDetailsModal({ open: true, personId: null, type: "websiteProfile" });
        }}
      />

      <NoSeatsRemainingModal show={showNoSeatsModal} onClose={() => setShowNoSeatsModal(false)} />

      <PersonDetailsModal
        modalType={personDetailsModal.type}
        show={personDetailsModal.open}
        personId={personDetailsModal.personId}
        onClose={() => {
          dispatch(getRoster());
          setPersonDetailsModal({ open: false, personId: null });
        }}
      />

      <DeleteAgentLenderModal
        agent={showDeleteAgentModal}
        lender={showDeleteLenderModal}
        onClose={(success) => {
          if (success) dispatch(getRoster());
          setShowDeleteLenderModal(null);
          setShowDeleteAgentModal(null);
        }}
      />

      <UnpublishedChangesModal blocker={blocker} />
    </div>
  );
};

export default Roster;
